import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Input, InputNumber, Switch, Select, Button, Space, Tag, Tooltip, Collapse, Typography, message, Spin } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import systemService from '../../services/systemService';
import { SETTINGS } from './settingsRegistry';

const { Title } = Typography;
const { TextArea } = Input;

// 只编辑有消费方的 cdn_global_settings 键（见 settingsRegistry / inventory）。
// default_cache_ttl / default_ssl_mode / default_http_version / default_gzip_enabled /
// default_brotli_enabled 无任何边缘或控制面读取 → 不展示（值保留在库里）。
// cdn_uam_secret 自动生成、属密钥 → 不展示。
const ITEMS = SETTINGS.filter((s) => s.source === 'cdn');

const toForm = (s, v) => {
  if (s.type === 'bool') return v === true || v === 1 || v === '1' || v === 'true';
  if (s.type === 'int') return v === null || v === undefined || v === '' ? undefined : Number(v);
  if (s.type === 'json') return v === null || v === undefined ? '' : JSON.stringify(v, null, 2);
  if (s.type === 'list' || s.type === 'domains') return Array.isArray(v) ? v : [];
  return v === null || v === undefined ? '' : String(v);
};

const fromForm = (s, v) => {
  if (s.type === 'bool') return !!v;
  if (s.type === 'int') return v === null || v === undefined ? 0 : Number(v);
  if (s.type === 'json') return v ? JSON.parse(v) : {};
  return v;
};

const CdnGlobalSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState({});
  const [envOverride, setEnvOverride] = useState({});
  const [rulesets, setRulesets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cdnService.getGlobalSettings();
      if (!r || !r.success) throw new Error(r?.error || '加载失败');
      const flat = {};
      Object.values(r.data || {}).forEach((g) => Object.entries(g || {}).forEach(([k, v]) => { flat[k] = v ? v.value : undefined; }));
      const init = {};
      ITEMS.forEach((s) => { init[s.key] = toForm(s, flat[s.key]); });
      setInitial(init);
      form.setFieldsValue(init);
    } catch (e) {
      message.error('加载 CDN 全局设置失败: ' + (e?.message || ''));
    } finally {
      setLoading(false);
    }
    try {
      const rt = await systemService.getRuntimeSettings();
      if (rt && rt.success) {
        const o = {};
        ITEMS.forEach((s) => { if (s.envOverride && rt.data?.[s.envOverride]?.raw) o[s.key] = s.envOverride; });
        setEnvOverride(o);
      }
    } catch (_) { /* ignore */ }
    try {
      const rs = await cdnService.getWafRulesetsAdmin();
      const list = Array.isArray(rs?.data) ? rs.data : [];
      setRulesets(list.filter((x) => x && x.key).map((x) => ({ value: x.key, label: `${x.name || x.key}` })));
    } catch (_) { /* ignore */ }
  }, [form]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    let values;
    try { await form.validateFields(); } catch (_) { return; }
    // getFieldsValue(true) 取全部字段（含折叠面板里的），避免未挂载字段读成 undefined 被误存
    values = form.getFieldsValue(true);
    // 只提交改动过的键，未改的行原样不动
    const changed = ITEMS.filter((s) => JSON.stringify(values[s.key]) !== JSON.stringify(initial[s.key]));
    if (changed.length === 0) { message.info('没有改动'); return; }
    setSaving(true);
    try {
      for (const s of changed) {
        const res = await cdnService.updateGlobalSetting({ key: s.key, value: fromForm(s, values[s.key]) });
        if (!res || !res.success) throw new Error(`${s.label}: ${res?.error || '保存失败'}`);
      }
      message.success(`已保存 ${changed.length} 项，节点下次拉取配置时生效`);
      load();
    } catch (e) {
      message.error(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const field = (s) => {
    const locked = !!envOverride[s.key];
    let control;
    if (s.type === 'bool') control = <Switch disabled={locked} />;
    else if (s.type === 'int') control = <InputNumber min={0} style={{ width: 240 }} disabled={locked} />;
    else if (s.type === 'enum') control = <Select style={{ width: 240 }} disabled={locked} options={Object.entries(s.options).map(([value, label]) => ({ value, label }))} />;
    else if (s.type === 'list') control = <Select mode="multiple" style={{ maxWidth: 520 }} disabled={locked} options={rulesets} placeholder="选择规则集" />;
    else if (s.type === 'domains') control = <Select mode="tags" style={{ maxWidth: 520 }} disabled={locked} open={false} tokenSeparators={[',', ' ']} placeholder="输入域名后回车，如 example.com" />;
    else if (s.type === 'json') control = <TextArea rows={6} style={{ fontFamily: 'monospace' }} disabled={locked} />;
    else control = <Input style={{ maxWidth: 520 }} disabled={locked} />;
    const rules = s.type === 'json' ? [{ validator: (_, v) => { if (!v) return Promise.resolve(); try { JSON.parse(v); return Promise.resolve(); } catch (e) { return Promise.reject(new Error('不是合法 JSON')); } } }] : [];
    return (
      <Form.Item
        key={s.key}
        name={s.key}
        valuePropName={s.type === 'bool' ? 'checked' : 'value'}
        tooltip={`${s.help}（${s.key}）`}
        rules={rules}
        label={
          <Space size={4}>
            {s.label}
            {s.danger && <Tag color="red">谨慎</Tag>}
            {locked && <Tooltip title={`被环境变量 ${envOverride[s.key]} 覆盖，此处不可改`}><Tag color="orange">被 .env 覆盖</Tag></Tooltip>}
          </Space>
        }
      >
        {control}
      </Form.Item>
    );
  };

  const common = ITEMS.filter((s) => !s.advanced);
  const advanced = ITEMS.filter((s) => s.advanced);

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>CDN 全局设置</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving}>保存</Button>
        </Space>
      </div>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Card size="small" style={{ marginBottom: 16 }}>{common.map(field)}</Card>
          <Collapse items={[{ key: 'adv', forceRender: true, label: `高级（${advanced.length}）`, children: advanced.map(field) }]} />
        </Form>
      </Spin>
    </div>
  );
};

export default CdnGlobalSettings;
